import { ComponentType, createContext, MouseEvent, ReactNode, useContext } from 'react';
import type { PolymorphicPropsWithoutRef } from 'react-polymorphic-types';

export const defaultElement = 'button';

export type TrackedClick = {
  // The clicked element's text, or 'Button' when it has none.
  label: string;
  component: string;
  params: { [key: string]: string | number };
};

// The design system doesn't know where clicks are recorded; the app decides by
// wrapping itself in a ClickTrackingProvider. Without one, clicks aren't tracked.
const ClickTrackingContext = createContext<(click: TrackedClick) => void>(() => {});

export const ClickTrackingProvider = ({
  onClick,
  children,
}: {
  onClick: (click: TrackedClick) => void;
  children: ReactNode;
}) => <ClickTrackingContext.Provider value={onClick}>{children}</ClickTrackingContext.Provider>;

type WithClickTracking = {
  eventParams?: { [key: string]: string | number };
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  track?: boolean;
};

function withClickTracking<Props>(WrappedComponent: ComponentType<Props>) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  type CompProps<T extends React.ElementType = typeof defaultElement> = PolymorphicPropsWithoutRef<
    Props & WithClickTracking,
    T
  >;

  function Comp<T extends React.ElementType = typeof defaultElement>({
    eventParams,
    onClick,
    as,
    track = true,
    ...rest
  }: CompProps<T>) {
    const trackClick = useContext(ClickTrackingContext);
    const Element: React.ElementType = as || defaultElement;

    const handleClick = (event: MouseEvent<HTMLElement>) => {
      if (!onClick) return;

      if (track) {
        const label = event.currentTarget.textContent?.trim() || 'Button';
        trackClick({ label, component: displayName, params: { label, ...eventParams } });
      }

      onClick(event);
    };

    return <WrappedComponent component={Element} {...(rest as Props)} onClick={handleClick} />;
  }

  Comp.displayName = `withClickTracking(${displayName})`;
  return Comp;
}

export default withClickTracking;
